import {
  ConfiguredDataSource,
  ConfiguredEntityResolver,
  DataCompartmentOptions,
  IAppStorage,
  RepositoryCompartmentOptions,
  createDataCache,
  createDataCacheModule,
  createRepository,
} from '@aesop-fables/scrinium';
import { IServiceContainer } from '@aesop-fables/containr';
import { ApiKeys } from '../../api/apis/ApiKeys';
import { RevShareApi } from '../../api/apis/RevShareApi';
import {
  OrgTransactionsData,
  TeamTransactionsData,
  GroupTransactionsData,
  AnyTransactionsData,
  RevShareResult,
} from '../../models/RevShareData';
import { AvailablePricebooks } from '../../models/PricebookData';
import { AdminTierTypeEnum } from '../../services/drillDown';
import {
  getRevShareCompartmentKey,
  createRevShareKey,
  parseRevShareKey,
  TransactionsDateRange,
} from './helpers/transactionsUtils';
import { GroupRevenueAccessResolved } from '../../predicates/GroupRevenueAccessResolved';
import { OrgAvailablePricebooksInitialized } from '../../predicates/OrgAvailablePricebooksInitialized';
import { TeamAvailablePricebooksInitialized } from '../../predicates/TeamAvailablePricebooksInitialized';
import { PricebookCompartments, pricebookStorageKey } from '../pricebook';
import { normalizeRevShareEntry } from './helpers/revShareEntryUtils';
import { firstValueFrom } from 'rxjs';

export interface RevShareRegistry {
  orgRevShareTransactions: RepositoryCompartmentOptions<string, OrgTransactionsData>;
  teamRevShareTransactions: RepositoryCompartmentOptions<string, TeamTransactionsData>;
  groupRevShareTransactions: RepositoryCompartmentOptions<string, GroupTransactionsData>;
}

export interface RevShareCompartments {
  orgRevShareSettings: DataCompartmentOptions<RevShareResult | undefined>;
  teamRevShareSettings: DataCompartmentOptions<RevShareResult | undefined>;
  groupRevShareSettings: DataCompartmentOptions<RevShareResult | undefined>;
}

export const revShareRepositoryKey = 'data/rev-share/transactions';
export const revShareStorageKey = 'data/rev-share/settings';

export const withRevShareData = createDataCacheModule(
  (appStorage: IAppStorage, container: IServiceContainer) => {
    const api = container.get<RevShareApi>(ApiKeys.RevShare);

    const repository = createRepository<RevShareRegistry>({
      orgRevShareTransactions: {
        resolver: new ConfiguredEntityResolver<string, OrgTransactionsData>(async key => {
          const { startDate, endDate } = parseRevShareKey(key);

          try {
            const { data } = await api.getOrgRevShare(startDate, endDate);
            return data;
          } catch (error) {
            console.error(
              `Error fetching org transaction data for ${startDate} - ${endDate}`,
              error,
            );
            return {} as OrgTransactionsData;
          }
        }),
      },
      teamRevShareTransactions: {
        resolver: new ConfiguredEntityResolver<string, TeamTransactionsData>(async key => {
          const { startDate, endDate } = parseRevShareKey(key);

          try {
            const { data } = await api.getTeamRevShare(startDate, endDate);
            return data;
          } catch (error) {
            console.error(
              `Error fetching team transaction data for ${startDate} - ${endDate}`,
              error,
            );
            return {} as TeamTransactionsData;
          }
        }),
      },
      groupRevShareTransactions: {
        resolver: new ConfiguredEntityResolver<string, GroupTransactionsData>(async key => {
          const { startDate, endDate } = parseRevShareKey(key);

          try {
            const { data } = await api.getGroupRevShare(startDate, endDate);
            return data;
          } catch (error) {
            console.error(
              `Error fetching group transaction data for ${startDate} - ${endDate}`,
              error,
            );
            return {} as GroupTransactionsData;
          }
        }),
      },
    });

    const fetchAllEntries = () =>
      new ConfiguredDataSource<RevShareResult | undefined>(async () => {
        try {
          const { data } = await api.getAllRevShareEntries();
          return { revenueShares: (data ?? []).map(normalizeRevShareEntry) };
        } catch (error) {
          console.error('Error fetching rev share settings', error);
          return undefined;
        }
      });

    const fetchEnrichedEntries = (
      byPricebookFetcher: (id: number) => Promise<{ data: RevShareResult }>,
      pricebookCompartmentKey: keyof PricebookCompartments,
    ) =>
      new ConfiguredDataSource<RevShareResult | undefined>(async () => {
        try {
          const pricebookCache = appStorage.retrieve<PricebookCompartments>(pricebookStorageKey);
          const available = await firstValueFrom(
            pricebookCache.observe$<AvailablePricebooks | undefined>(pricebookCompartmentKey),
          );
          const pricebookIds = (available?.pricebookBundleDtos ?? []).map(b => b.pricebook.id);

          if (pricebookIds.length === 0) {
            return { revenueShares: [] };
          }

          const byPricebookResults = await Promise.allSettled(
            pricebookIds.map(id => byPricebookFetcher(id)),
          );

          const entries = byPricebookResults
            .filter(
              (r): r is PromiseFulfilledResult<{ data: RevShareResult }> =>
                r.status === 'fulfilled',
            )
            .flatMap(r => (r.value.data.revenueShares ?? []).map(normalizeRevShareEntry));

          return { revenueShares: entries };
        } catch (error) {
          console.error('Error fetching enriched rev share settings', error);
          return undefined;
        }
      });

    const cache = createDataCache<RevShareCompartments>({
      orgRevShareSettings: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(OrgAvailablePricebooksInitialized),
        },
        source: fetchEnrichedEntries(
          id => api.getOrgRevShareResultByPricebook(id),
          'orgAvailablePricebooks',
        ),
        defaultValue: undefined,
      },
      teamRevShareSettings: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(TeamAvailablePricebooksInitialized),
        },
        source: fetchEnrichedEntries(
          id => api.getTeamRevShareResultByPricebook(id),
          'teamAvailablePricebooks',
        ),
        defaultValue: undefined,
      },
      groupRevShareSettings: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(GroupRevenueAccessResolved),
        },
        source: fetchAllEntries(),
        defaultValue: undefined,
      },
    });

    appStorage.store(revShareRepositoryKey, repository);
    appStorage.store(revShareStorageKey, cache);
  },
);

// Repository helpers
export const fetchRevShareTransactions = async (
  storage: IAppStorage,
  dateRange: TransactionsDateRange,
  tier: AdminTierTypeEnum,
) => {
  const compartmentKey = getRevShareCompartmentKey(tier);
  if (!compartmentKey || !dateRange.startDate || !dateRange.endDate) return;

  const key = createRevShareKey(dateRange.startDate, dateRange.endDate);
  const repository = storage.repository<RevShareRegistry>(revShareRepositoryKey);
  const compartment = repository.get(compartmentKey, key);

  try {
    let data = await compartment.getData();
    if (!data) {
      await compartment.reload();
      data = await compartment.getData();
    }
    return data as AnyTransactionsData;
  } catch (error) {
    console.error('Error fetching transaction data:', error);
    return;
  }
};

export function getCachedRevShareTransactions(
  storage: IAppStorage,
  range: TransactionsDateRange,
  tier: AdminTierTypeEnum,
): AnyTransactionsData | undefined {
  const compartmentKey = getRevShareCompartmentKey(tier);
  if (!compartmentKey || !range.startDate || !range.endDate) return undefined;

  const id = createRevShareKey(range.startDate, range.endDate);
  const repo = storage.repository<RevShareRegistry>(revShareRepositoryKey);
  const compartment = repo.get<typeof id, AnyTransactionsData>(compartmentKey, id);

  return compartment.getData() as AnyTransactionsData;
}

export const clearRevShareTransactions = (storage: IAppStorage): void => {
  const repository = storage.repository<RevShareRegistry>(revShareRepositoryKey);
  repository.clearAll('orgRevShareTransactions');
  repository.clearAll('teamRevShareTransactions');
  repository.clearAll('groupRevShareTransactions');
};
